import React from 'react'

import globalNotification from 'live/services/global-notification'

import { deleteNodeAndItsChildren } from '../importer-api'
import { GlobalNotification } from '../global-components/GlobalNotification'
import { TemporaryDataType, TemporarySetDataType } from '../temporary-data-table/useTemporaryDataState'
import { Row } from '../temporary-data-table/editable-table-utils'

export const deleteEntityAndItsChildren = (rowId: number, isExporting?: boolean): Promise<void> => {
    return deleteNodeAndItsChildren(rowId)
        .then(status => {
            if (status == 200) {
                globalNotification.success(
                    <GlobalNotification
                        title={`${isExporting ? 'Draft table' : 'Node entity'} was successfully deleted.`}
                    />,
                    7
                )
            } else {
                throw Error('Status = ' + status)
            }
        })
        .catch(err => {
            globalNotification.error(
                <GlobalNotification title="Error! Could not delete node entity." type="error" />,
                7
            )
            throw err
        })
}

export const deletedCascadeEntities = (data: Record<string, Entity>, deletedRow: TemporaryEntity) => {
    if (!data) return
    let entities: TemporaryEntity[] = []

    Object.values(data).forEach(values => {
        entities = entities.concat(values)
    })

    let queue = [deletedRow.frontUuid]
    let deletedChildren = [deletedRow.frontUuid]

    while (queue.length > 0) {
        const currentUuid = queue.pop()
        if (!currentUuid) break

        const currentDeletedChildren = entities
            .filter(value => value.frontParentUuid === currentUuid)
            .map(c => c.frontUuid)
        deletedChildren = deletedChildren.concat(currentDeletedChildren)

        queue = queue.concat(currentDeletedChildren)
    }

    let result = {} as Record<string, Entity>

    for (const [key, values] of Object.entries(data)) {
        const newData = values?.filter(value => !deletedChildren.includes(value.frontUuid))

        if (newData.length) {
            result = {
                ...result,
                [key]: newData as Entity
            }
        }
    }

    return result
}

export const clearCheckedData = (setCheckedData: TemporarySetDataType) => {
    setCheckedData.setWells([])
    setCheckedData.setInterventions([])
    setCheckedData.setSections([])
    setCheckedData.setRuns([])
    setCheckedData.setRigTables([])
    setCheckedData.setTubulars([])
    setCheckedData.setCements([])
    setCheckedData.setPipeMovements([])
    setCheckedData.setRops([])
    setCheckedData.setConnections([])
}

export const updateCheckedDataByEntitiesState = (
    newData: TemporaryDataType,
    currentCheckedDataByEntities: Record<string, Entity>,
    setter: (value: Record<string, Entity>) => void,
    updatedData?: Row
) => {
    if (!currentCheckedDataByEntities) return
    let entities: TemporaryEntity[] = []

    Object.values(newData).forEach(values => {
        entities = entities.concat(values)
    })

    const entitiesRowId = entities.map(entity => entity.rowId)

    let newCheckedDataByEntities = {}

    for (const [key, values] of Object.entries(currentCheckedDataByEntities)) {
        values.forEach(value => {
            if (entitiesRowId.includes(value.rowId)) {
                const [entity, nodeId] = key.split('=>')
                if (!newCheckedDataByEntities[key]) {
                    newCheckedDataByEntities[key] = []
                }
                const newValue =
                    updatedData?.rowId && value?.rowId === updatedData?.rowId
                        ? updatedData
                        : newData[entity]?.find(item => item?.rowId === value?.rowId)

                newCheckedDataByEntities[key].push(newValue ?? value)
            }
        })
    }

    setter(newCheckedDataByEntities)
}
